/** The one place a mount record becomes a word. Nothing else reads `MountState`. */

import { mountStateLabel } from "@/lib/spaces";

import type { MountRecord, MountState } from "../types";

/** What an added activity is doing. `paused` and `failed` are not mount states. */
export type RunStatus = MountState | "paused" | "failed";

export type RunTone = "neutral" | "success" | "warning" | "danger" | "info";

/** An activity verb. Five act on one added activity; a space verb cascades onto each of them. */
export type ActivityVerb = "Start" | "Stop" | "Restart" | "Pause" | "Resume" | "Reset progress";

export const ACTIVITY_VERBS: readonly ActivityVerb[] = [
  "Start",
  "Stop",
  "Restart",
  "Pause",
  "Resume",
  "Reset progress",
];

export type SpaceVerb = "Load" | "Unload" | "Start" | "Stop" | "Pause";

export const SPACE_VERBS: readonly SpaceVerb[] = ["Load", "Unload", "Start", "Stop", "Pause"];

/** The status of one added activity, read off the mount it holds. */
export function runStatusOf(mount: MountRecord | null): RunStatus {
  if (mount === null) return "ended";
  if (mount.end_reason === "refused") return "failed";
  const state = mount.state ?? "ended";
  if (state === "running" && mount.paused_at != null) return "paused";
  return state;
}

const OFF_STATE_LABELS: Record<"paused" | "failed", string> = {
  paused: "Paused",
  failed: "Failed",
};

/** The word for a status. Mount states go through `mountStateLabel` and are never spelled here. */
export function runLabel(status: RunStatus): string {
  return status === "paused" || status === "failed"
    ? OFF_STATE_LABELS[status]
    : mountStateLabel(status);
}

const TONES: Record<RunStatus, RunTone> = {
  starting: "info",
  running: "success",
  paused: "warning",
  ending: "neutral",
  ended: "neutral",
  failed: "danger",
};

export function runTone(status: RunStatus): RunTone {
  return TONES[status];
}

/** Whether this status counts as live: something is up, even if it is holding. */
export function isLive(status: RunStatus): boolean {
  return status === "starting" || status === "running" || status === "paused";
}

/** Which activity verbs this status admits. */
export function verbsFor(status: RunStatus): readonly ActivityVerb[] {
  if (status === "paused") return ["Resume", "Restart", "Stop", "Reset progress"];
  if (isLive(status)) return ["Pause", "Restart", "Stop", "Reset progress"];
  return ["Start", "Reset progress"];
}
